import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide9cToolBridge.copy'

const ease = [0.16, 1, 0.3, 1] as const

type Phase = 'idle' | 'processing' | 'result'

export default function Slide9cToolBridge(_props: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['fakeDomain', 'sampleMessage'],
  })
  const TABS = [c.tabLink, c.tabMessage, c.tabImage, c.tabSituation]
  const FINDINGS = [c.finding1, c.finding2, c.finding3]
  const PROCESSING_STEPS = [c.processing1, c.processing2, c.processing3]

  const [phase, setPhase] = useState<Phase>('idle')
  const [processingStep, setProcessingStep] = useState(0)

  const handleAnalyse = () => {
    setPhase('processing')
    setProcessingStep(0)

    const step1 = setTimeout(() => setProcessingStep(1), 900)
    const step2 = setTimeout(() => setProcessingStep(2), 1800)
    const done = setTimeout(() => setPhase('result'), 2800)

    return () => {
      clearTimeout(step1)
      clearTimeout(step2)
      clearTimeout(done)
    }
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto max-w-5xl">
      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3, ease }}
        className="mt-5 text-4xl sm:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.05]"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.65, ease }}
        className="mt-6 text-lg sm:text-xl text-slate-500 leading-relaxed max-w-4xl"
      >
        {c.introBefore}
        <Link to="/tools" className="font-medium text-violet-600 hover:text-violet-700 transition-colors underline underline-offset-2">
          {c.toolsLinkLabel}
        </Link>
        {c.introAfter}
      </motion.p>

      {/* Scam Checker preview */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0, ease }}
        className="mt-10 w-full max-w-xl"
      >
        <div className="rounded-2xl bg-white p-5 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-6">
          <AnimatePresence mode="wait">
            {phase === 'idle' && (
              <motion.div
                key="idle"
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease }}
              >
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-violet-50 text-violet-600">
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.3-4.3" />
                      <path d="M8 11l2 2 4-4" />
                    </svg>
                  </div>
                  <div className="text-start">
                    <p className="text-lg font-semibold text-slate-900">
                      {c.scamCheckerName}
                    </p>
                    <p className="text-base text-slate-400">
                      {c.scamCheckerSubtitle}
                    </p>
                  </div>
                </div>

                {/* Segmented control */}
                <div className="mt-4 grid grid-cols-4 rounded-xl bg-slate-100/80 p-1">
                  {TABS.map((tab, i) => (
                    <div
                      key={tab}
                      className={`rounded-lg px-3 py-2.5 text-center text-sm font-medium ${
                        i === 1
                          ? 'bg-white text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
                          : 'text-slate-400'
                      }`}
                    >
                      {tab}
                    </div>
                  ))}
                </div>

                {/* Pre-filled input */}
                <div className="mt-3 rounded-xl border border-slate-200 px-4 py-3.5">
                  <p className="text-base text-slate-500 text-start leading-relaxed">
                    {c.sampleMessage}
                  </p>
                </div>

                {/* Analyse button */}
                <div className="mt-3">
                  <button
                    onClick={handleAnalyse}
                    className="w-full rounded-xl bg-violet-600 py-3 text-center text-base font-semibold text-white hover:bg-violet-700 transition-colors cursor-pointer"
                  >
                    {c.analyseLabel}
                  </button>
                </div>
              </motion.div>
            )}

            {phase === 'processing' && (
              <motion.div
                key="processing"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease }}
                className="flex flex-col items-center py-8"
              >
                {/* Spinner */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                  className="h-8 w-8 rounded-full border-[2.5px] border-slate-200 border-t-violet-500"
                />

                <div className="mt-5 space-y-2">
                  {PROCESSING_STEPS.map((step, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: processingStep >= i ? 1 : 0.3 }}
                      transition={{ duration: 0.3, ease }}
                      className="text-sm text-slate-500"
                    >
                      {processingStep > i && (
                        <span className="me-1.5 text-emerald-500">&#10003;</span>
                      )}
                      {step}
                    </motion.p>
                  ))}
                </div>
              </motion.div>
            )}

            {phase === 'result' && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease }}
              >
                {/* Verdict */}
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50">
                    <svg
                      className="h-5 w-5 text-rose-500"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                  <div className="text-start">
                    <p className="text-base font-semibold text-rose-600">
                      {c.verdictLabel}
                    </p>
                    <p className="text-sm text-slate-400">
                      {c.verdictSubtitle}
                    </p>
                  </div>
                </div>

                {/* Findings */}
                <div className="mt-4 space-y-2.5">
                  {FINDINGS.map((finding, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.15 + i * 0.12, ease }}
                      className="flex items-start gap-2.5 text-start"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 9L9 3M3 3l6 6" />
                        </svg>
                      </span>
                      <p className="text-sm leading-snug text-slate-600">
                        {finding}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* Recommendation */}
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.6, ease }}
                  className="mt-4 rounded-xl bg-slate-50 px-4 py-3"
                >
                  <p className="text-sm text-slate-500 text-start leading-relaxed">
                    <span className="font-semibold text-slate-700">{c.recommendationBoldPrefix}</span>{' '}
                    {c.recommendation}
                  </p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

    </div>
  )
}
